/**
 * "Run Comparison" — the events-index action that spins up a what-if duplicate
 * of an event and puts it in front of the florist.
 *
 * ONE destination: the comparison, in a new tab. The tab the florist started
 * from (the events list) is left exactly as it was, which is also where the
 * original event stays one click away — see "why only one tab" below.
 *
 * ─── WHY THIS IS SHAPED THE WAY IT IS ────────────────────────────────────────
 *
 * This feature broke three times, always for the same reason: it tried to open
 * a sized browser WINDOW for the comparison. The last version made two
 * `window.open` calls inside one click, then set a form's `target` to the second
 * window's name and let the browser submit it. `window.open` CONSUMES transient
 * user activation, so the second call had none left, Chrome's popup blocker
 * refused it, and `form.target` then named a window that did not exist — making
 * the native submission itself an auxiliary-window creation, also refused. Net
 * effect: no window, no navigation, and **no POST ever reached the server**, so
 * no comparison was created at all.
 *
 * The window is gone. `window.open` was the only reason transient activation,
 * the popup blocker, `_blank`-vs-window-name and a blocked-popup fallback ever
 * appeared in this code, and deleting it removes that whole failure mode rather
 * than working around it. **Do not reintroduce `window.open` here** — a spec
 * asserts it is never called.
 *
 * Two rules remain, and both are load-bearing:
 *
 *   1. WHETHER THE RECORD EXISTS MUST NOT DEPEND ON A BROWSER OPENING ANYTHING.
 *      The comparison is created by a `fetch`, which no blocker can veto. The
 *      tab is only where we show it. This decoupling is the actual fix.
 *   2. THE TAB IS OPENED BY A SYNTHESIZED ANCHOR CLICK, not `window.open`.
 *
 * ─── WHY ONLY ONE TAB ────────────────────────────────────────────────────────
 *
 * An earlier version also opened the ORIGINAL event as a second tab, so the two
 * could be read side by side. Live testing found only ONE of the two actually
 * opening. The tabs are opened from a promise callback — the POST has to come
 * back before the comparison's URL even exists — so they are no longer in the
 * click's own task, and a browser may honour only one script-opened tab per
 * gesture.
 *
 * Reordering so the comparison went first was a mitigation, not a fix: it relies
 * on the browser honouring the FIRST request, which we cannot verify everywhere,
 * and a browser that honours the last would lose the comparison again. A refused
 * tab is undetectable, so we could not even tell the florist. And the refusal
 * can raise a "pop-up blocked" indicator, which reads as failure on a flow that
 * actually succeeded.
 *
 * So we spend the one tab we can rely on for the destination that matters, and
 * leave the original where it already is: on the events list the florist just
 * came from, one click away, in a tab this flow deliberately never touches.
 *
 * DO NOT ADD A SECOND `openInNewTab` CALL HERE. The comparison must stay the
 * only thing opened; a spec asserts exactly one tab.
 */

declare global {
  interface Window {
    showError?: (message: string, duration?: number) => void
    /** Guard so repeated Turbo renders don't stack duplicate click listeners. */
    __blumeRunComparisonInit?: boolean
  }
}

export interface RunComparisonOptions {
  /** POST endpoint that creates the comparison (a trigger's data-comparison-url). */
  comparisonUrl: string
  /** The original event's name, used in the failure message. */
  eventName?: string | null
}

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content') || ''
  )
}

/** Escapes text before it's interpolated into a toast's innerHTML. */
function escapeHtml(value: unknown): string {
  const div = document.createElement('div')
  div.textContent = value == null ? '' : String(value)
  return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

/**
 * Opens `url` in a new tab by clicking a throwaway anchor — see rule 2 above for
 * why it is an anchor rather than `window.open`. `rel="noopener"` because the
 * tab needs no handle back here.
 */
function openInNewTab(url: string): void {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener'
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

/**
 * Creates the comparison, then opens it in a new tab.
 *
 * Never rejects — failures surface as a toast in the tab the florist is still
 * looking at.
 */
export function runComparison({
  comparisonUrl,
  eventName,
}: RunComparisonOptions): Promise<void> {
  if (!comparisonUrl) {
    window.showError?.(
      "Something went wrong opening that comparison. Reload the events list and try again."
    )
    return Promise.resolve()
  }

  // The record first, unconditionally. A plain fetch rather than ApiClient:
  // there is no request body, so no Content-Type should be sent (ApiClient
  // always sets one, and an empty JSON body is a needless way to fail).
  return fetch(comparisonUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
  })
    .then(async (response) => {
      const payload = await response.json().catch(() => null)
      if (!response.ok || !payload?.url) {
        throw new Error(
          payload?.error || `Comparison request failed (${response.status})`
        )
      }
      return String(payload.url)
    })
    .then(
      (url) => {
        // Exactly one tab, and it is the comparison — see "why only one tab" at
        // the top of the file before adding a second call here.
        openInNewTab(url)
        // Deliberately nothing else. A refused tab is not observable from here,
        // so there is no toast about it: the previous version's "opened in a
        // plain tab instead of its own window" message asserted a state the code
        // could not detect, and the same rule applies to a tab that never
        // appeared. The comparison exists and is on the events list either way.
      },
      // Two-arg then, not .catch(): this handler must see failures from the
      // request only. A throw from the success branch above would mean the
      // comparison DID get created, and reporting "we couldn't" would be wrong.
      () => {
        window.showError?.(
          `We couldn’t create a comparison of “${escapeHtml(eventName || 'this event')}”. ` +
            `Check your events list, then try again.`,
          8000
        )
      }
    )
    // Keeps the "never rejects" contract: callers fire this and forget, so a
    // throw out of the success branch would otherwise become an unhandled
    // rejection. It deliberately does NOT claim failure — by here the
    // comparison exists.
    .catch(() => {})
}

/**
 * Populates and opens the shared explainer modal for one trigger. Every value
 * is read off THE TRIGGER and captured in this closure — nothing is written to
 * shared modal state, which is how a previous version ended up POSTing to `""`
 * (i.e. to /events) when a stale form action was reused.
 */
export function openRunComparisonModal(trigger: HTMLElement): void {
  const modal = document.getElementById('runComparisonModal')
  const confirmBtn = document.getElementById('confirmRunComparisonModal')
  const cancelBtn = document.getElementById('cancelRunComparisonModal')
  if (!modal || !confirmBtn || !cancelBtn) return

  const comparisonUrl = trigger.dataset.comparisonUrl || ''
  const eventName = trigger.dataset.eventName || ''

  const nameEl = document.getElementById('runComparisonEventName')
  if (nameEl) nameEl.textContent = eventName || 'this event'

  const closeBtn = document.getElementById('closeRunComparisonModal')
  const backdrop = modal.querySelector<HTMLElement>('.modal-overlay')

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') close()
  }
  function close() {
    modal!.classList.add('hidden')
    document.removeEventListener('keydown', onKeydown)
    confirmBtn!.onclick = null
  }

  if (closeBtn) closeBtn.onclick = close
  cancelBtn.onclick = close
  if (backdrop) backdrop.onclick = close
  document.addEventListener('keydown', onKeydown)

  confirmBtn.onclick = () => {
    close()
    runComparison({ comparisonUrl, eventName })
  }

  modal.classList.remove('hidden')
  // Focus the safe action so a stray Enter doesn't confirm immediately.
  cancelBtn.focus()
}

/**
 * One delegated document listener for every `.run-comparison-btn` on the page,
 * so the triggers keep working across Turbo Drive navigation.
 */
export function initRunComparisonModal(): void {
  if (window.__blumeRunComparisonInit) return
  window.__blumeRunComparisonInit = true

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null
    const trigger = target?.closest?.('.run-comparison-btn')
    if (!(trigger instanceof HTMLElement)) return

    event.preventDefault()
    event.stopPropagation()
    // Close the kebab menu so the modal isn't rendered under an open dropdown
    // (mirrors the delete-event trigger).
    document
      .querySelectorAll('.dropdown-menu:not(.hidden)')
      .forEach((menu) => menu.classList.add('hidden'))

    openRunComparisonModal(trigger)
  })
}
